import React, { useState } from 'react';
import {
  Box,
  Stack,
  Grid,
  Typography,
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';

import {
  Button,
  type DropDownSelectOption,
  DropdownSelect,
  Select,
  TextField,
} from '../';
import { PAGE_TEXT } from '../../pages/Assessment/lang';
import { type Filter } from './types';

import { COMMON_FILTER_TEXT } from '../../pages';

export type TableFilterV2Props = {
  filters: Record<string, Filter>;
  defaultAppliedFilter?: Record<string, string | string[]>;
  onFilterChange: (appliedFilter: Record<string, string | string[]>) => void;
};

export const TableFilterV2: React.FunctionComponent<TableFilterV2Props> = (
  props: TableFilterV2Props,
) => {
  const { filters, onFilterChange, defaultAppliedFilter } = props;
  const [appliedFilter, setAppliedFilter] = useState(
    defaultAppliedFilter ?? {},
  );
  const [expand, setExpand] = useState<boolean>(true);

  React.useEffect(() => {
    setAppliedFilter(defaultAppliedFilter ?? {});
  }, [defaultAppliedFilter]);

  const handleApply = (): void => {
    onFilterChange(appliedFilter);
  };

  const handleClear = (): void => {
    const newAppliedFilter = {};
    setAppliedFilter(newAppliedFilter);
    onFilterChange(newAppliedFilter);
  };

  const handleDropdownChange =
    (id: string) =>
    (selectedValue: string[]): void => {
      const newAppliedFilterMap = new Map(Object.entries(appliedFilter));
      const selectedValuesArr: string[] = Object.values(selectedValue);
      if (selectedValuesArr?.length) {
        newAppliedFilterMap.set(id, selectedValuesArr);
      } else {
        newAppliedFilterMap.delete(id);
      }
      const newAppliedFilter = Object.fromEntries(
        newAppliedFilterMap.entries(),
      );
      setAppliedFilter(newAppliedFilter);
    };

  const handleSingleSelectChange =
    (id: string) =>
    (
      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ): void => {
      const newAppliedFilterMap = new Map(Object.entries(appliedFilter));
      const value = event.target.value;
      const trimedValue = value.trim();
      if (trimedValue) {
        newAppliedFilterMap.set(id, trimedValue);
      } else {
        newAppliedFilterMap.delete(id);
      }
      const newAppliedFilter = Object.fromEntries(
        newAppliedFilterMap.entries(),
      );
      setAppliedFilter(newAppliedFilter);
    };

  const handleStringChange =
    (id: string) =>
    (
      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ): void => {
      const newAppliedFilterMap = new Map(Object.entries(appliedFilter));
      const value = event.target.value;
      const trimedValue = value.trim();
      if (trimedValue) {
        newAppliedFilterMap.set(id, trimedValue);
      } else {
        newAppliedFilterMap.delete(id);
      }
      const newAppliedFilter = Object.fromEntries(
        newAppliedFilterMap.entries(),
      );
      setAppliedFilter(newAppliedFilter);
    };

  const handleExpandChange = (): void => {
    setExpand(!expand);
  };

  const getFilteringConditionText = (): string => {
    const conditionText: string = Object.entries(appliedFilter)
      ?.map(([id, selectedFilterOption]) => {
        const filterItem = filters?.[id] ?? {};
        const { displayLabel = true, options, title } = filterItem;
        if (Array.isArray(selectedFilterOption)) {
          // map back the label
          const displayText = selectedFilterOption
            ?.map(selectedOption => {
              if (displayLabel) {
                const option = options?.find(
                  (option: DropDownSelectOption) =>
                    option.value === selectedOption,
                );
                return option?.label ?? selectedOption;
              }
              return selectedOption;
            })
            .join(', ');
          return `${title}: ${displayText}`;
        } else if (typeof selectedFilterOption === 'string') {
          return `${title}: ${selectedFilterOption}`;
        }
        return null;
      })
      .filter(item => item !== null)
      .join(', ');

    return conditionText === ''
      ? '[Selected: All]'
      : `[Selected Conditions: ${conditionText}]`;
  };

  return (
    <Accordion className="w-full" expanded={expand}>
      <AccordionSummary
        onClick={handleExpandChange}
        style={{
          background: '#F1F1F1',
          color: '#757070',
          width: '100%',
        }}
      >
        <div className="flex flex-col w-full">
          <div className="flex items-start">
            {expand ? (
              <ExpandLess
                style={{
                  transform: 'rotate(180deg)',
                  color: 'grey',
                  marginRight: 10,
                }}
              />
            ) : (
              <ExpandMore
                style={{
                  transform: 'rotate(270deg)',
                  color: 'grey',
                  marginRight: 10,
                }}
              />
            )}
            <strong>{COMMON_FILTER_TEXT.TITLE}</strong>
          </div>
          <div className="flex flex-row justify-between">
            <i
              className="overflow-ellipsis overflow-hidden whitespace-nowrap pl-8 text-sm"
              title={getFilteringConditionText()}
            >
              {getFilteringConditionText()}
            </i>
          </div>
        </div>
      </AccordionSummary>
      <AccordionDetails className="bg-[#F1F1F1]">
        <div>
          <Stack ml={4}>
            <div className="w-full mb-4">
              <Grid container rowSpacing={2}>
                {Object.entries(filters).map(([id, filter]) => {
                  const { title, type, options, displayLabel = true } = filter;
                  return (
                    <Grid
                      key={id}
                      className="flex items-center justify-start"
                      size={{ xs: 12, md: 6 }}
                    >
                      <Grid
                        container
                        spacing={2}
                        alignItems="center"
                        sx={{ width: '100%' }}
                        key={title + id + 'item'}
                      >
                        <Grid key={title + id + 'title'} size={4}>
                          <Typography variant="body2">{title}</Typography>
                        </Grid>
                        <Grid key={title + id + 'input'} size={8}>
                          {type === 'select' ? (
                            <DropdownSelect
                              key={id + type}
                              options={options ?? []}
                              value={(appliedFilter?.[id] ?? []) as string[]}
                              onChange={handleDropdownChange(id)}
                              displayLabel={displayLabel}
                            />
                          ) : type === 'singleSelect' ? (
                            <Select
                              key={id + type}
                              sx={{
                                margin: '8px',
                                width: '90%',
                                maxWidth: '340px',
                              }}
                              size="small"
                              options={options ?? []}
                              value={(appliedFilter?.[id] ?? []) as string[]}
                              onChange={handleSingleSelectChange(id)}
                            />
                          ) : (
                            <TextField
                              key={id + type}
                              sx={{
                                margin: '8px',
                                width: '90%',
                                maxWidth: '340px',
                              }}
                              size="small"
                              value={appliedFilter?.[id] ?? ''}
                              onChange={handleStringChange(id)}
                            />
                          )}
                        </Grid>
                      </Grid>
                    </Grid>
                  );
                })}
              </Grid>
            </div>

            <Box
              display={'flex'}
              flexDirection={'row'}
              justifyContent={'center'}
            >
              <Button
                variant="contained"
                type="submit"
                onClick={handleApply}
                sx={{ textTransform: 'none' }}
              >
                {PAGE_TEXT.APPLY}
              </Button>
              <Button
                variant="text"
                onClick={handleClear}
                sx={{ textTransform: 'none' }}
              >
                {PAGE_TEXT.CLEAR}
              </Button>
            </Box>
          </Stack>
        </div>
      </AccordionDetails>
    </Accordion>
  );
};
